// Guest profile page — manage personal info, registered card, billing

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import toast from 'react-hot-toast';

export default function ProfilePage() {

const API_BASE = window.location.hostname === 'localhost' ? '/api' : 'https://purplemine.mkim0818.workers.dev/api';
  const { user, token, loadUser } = useAuthStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'info' | 'card'>('info');
  const [form, setForm] = useState({
    name: '', phone: '',
    is_business: false,
    business_number: '', business_name: '', business_phone: '',
    business_address: '', tax_email: '',
  });
  const [saving, setSaving] = useState(false);
  const [cardInfo, setCardInfo] = useState<{ lastFour?: string | null; billingKeyStatus?: string }>({});
  const [loadingCard, setLoadingCard] = useState(true);
  const [registeringCard, setRegisteringCard] = useState(false);

  useEffect(() => {
    if (!user) return;
    const b = !!(user.business_number || user.business_name);
    setForm({
      name: user.name || '',
      phone: user.phone || '',
      is_business: b,
      business_number: user.business_number || '',
      business_name: user.business_name || '',
      business_phone: (user as any).business_phone || '',
      business_address: (user as any).business_address || '',
      tax_email: (user as any).tax_email || '',
    });

    fetch(API_BASE + '/auth/card-info', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        setCardInfo({
          lastFour: d.card_last_four || null,
          billingKeyStatus: d.billing_key_status || 'none',
        });
        setLoadingCard(false);
      })
      .catch(() => setLoadingCard(false));
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data: any = { ...form };
      if (!data.is_business) {
        data.business_number = ''; data.business_name = ''; data.business_phone = '';
        data.business_address = ''; data.tax_email = '';
      }
      const r = await fetch(API_BASE + '/auth/update-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error('저장 실패');
      toast.success('프로필이 업데이트되었습니다');
      loadUser();
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteCard = async () => {
    if (!confirm('등록된 카드를 삭제하시겠습니까? 다음 예약 시 다시 등록해야 합니다.')) return;
    try {
      const r = await fetch(API_BASE + '/auth/remove-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('삭제 실패');
      setCardInfo({ lastFour: undefined, billingKeyStatus: 'none' });
      toast.success('카드가 삭제되었습니다');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRegisterCard = () => {
    setRegisteringCard(true);
    const customerKey = user?.id || '';
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/profile?cardRegistered=1&customerKey=${customerKey}`;
    const failUrl = `${baseUrl}/profile?cardError=1`;

    try {
      const TossPayments = (window as any).TossPayments;
      if (!TossPayments) {
        const script = document.createElement('script');
        script.src = 'https://js.tosspayments.com/v2';
        script.onload = () => {
          (window as any).TossPayments('test_ck_placeholder').payment()
            .requestBillingAuth({ method: 'CARD', customerKey, successUrl, failUrl });
        };
        document.head.appendChild(script);
      } else {
        TossPayments('test_ck_placeholder').payment()
          .requestBillingAuth({ method: 'CARD', customerKey, successUrl, failUrl });
      }
    } catch (err: any) { toast.error('토스 SDK 로드 실패'); setRegisteringCard(false); }
  };

  // Handle Toss card registration callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authKey = params.get('authKey');
    const customerKey = params.get('customerKey');
    if (authKey && customerKey) {
      window.history.replaceState({}, '', window.location.pathname);
      // Register card via auth endpoint (no reservation needed)
      fetch(API_BASE + '/auth/register-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ authKey, customerKey }),
      }).then(async r => {
        if (r.ok) {
          toast.success('카드가 등록되었습니다');
          const cr = await fetch(API_BASE + '/auth/card-info', { headers: { Authorization: `Bearer ${token}` } });
          const cd = await cr.json();
          setCardInfo({ lastFour: cd.card_last_four, billingKeyStatus: cd.billing_key_status });
        } else {
          toast.error('카드 등록 실패');
        }
        setRegisteringCard(false);
      }).catch(() => { toast.error('카드 등록 실패'); setRegisteringCard(false); });
    }
  }, []);

  if (!user) { navigate('/signin'); return null; }

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <button onClick={() => navigate(-1)} className="text-sm text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
        뒤로
      </button>

      <h1 className="text-2xl font-bold text-slate-900 mb-2">내 정보</h1>
      <p className="text-sm text-gray-500 mb-6">{user.email}</p>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        <button onClick={() => setTab('info')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${tab === 'info' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          👤 프로필
        </button>
        <button onClick={() => setTab('card')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 ${tab === 'card' ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          💳 결제 카드
        </button>
      </div>

      {tab === 'info' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">이름</label>
            <input className="input-field text-sm" value={form.name}
              onChange={e => setForm(f => ({...f, name: e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">연락처</label>
            <input className="input-field text-sm" value={form.phone}
              onChange={e => setForm(f => ({...f, phone: e.target.value}))} />
          </div>

          {/* Business customer toggle */}
          <div className="border-t border-gray-100 pt-3 mt-3">
            <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-50 rounded-xl">
              <input
                type="checkbox"
                checked={form.is_business}
                onChange={e => setForm(f => ({...f, is_business: e.target.checked}))}
                className="w-4 h-4 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">기업 고객</span>
                <p className="text-xs text-gray-400">세금계산서 발행이 필요한 기업 고객입니다</p>
              </div>
            </label>

            {form.is_business && (
              <div className="mt-3 space-y-3 animate-in">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">회사명</label>
                    <input className="input-field text-sm" value={form.business_name}
                      onChange={e => setForm(f => ({...f, business_name: e.target.value}))} placeholder="(주)제작사명" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">회사 전화번호</label>
                    <input className="input-field text-sm" value={form.business_phone}
                      onChange={e => setForm(f => ({...f, business_phone: e.target.value}))} placeholder="02-1234-5678" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">사업자등록번호</label>
                  <input className="input-field text-sm" value={form.business_number}
                    onChange={e => setForm(f => ({...f, business_number: e.target.value}))} placeholder="000-00-00000" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">사업장 주소</label>
                  <input className="input-field text-sm" value={form.business_address}
                    onChange={e => setForm(f => ({...f, business_address: e.target.value}))} />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">세금계산서 수신 이메일</label>
                  <input className="input-field text-sm" value={form.tax_email}
                    onChange={e => setForm(f => ({...f, tax_email: e.target.value}))} placeholder="tax@company.co.kr" />
                </div>
              </div>
            )}
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full mt-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}

      {tab === 'card' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-semibold text-slate-900 mb-4">등록된 결제 카드</h3>

          {loadingCard ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" /></div>
          ) : cardInfo.lastFour ? (
            <div className="bg-emerald-50 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <p className="text-sm font-mono font-bold text-slate-800">•••• •••• •••• {cardInfo.lastFour}</p>
              <p className="text-xs text-emerald-600 mt-1">등록 완료 · {cardInfo.billingKeyStatus === 'active' ? '사용 가능' : '만료됨'}</p>
              <p className="text-xs text-gray-400 mt-3">
                모든 예약에 이 카드가 사용됩니다. 반납 후 최종 금액이 청구됩니다.
              </p>
              <button onClick={handleDeleteCard}
                className="mt-4 px-4 py-2 text-sm text-red-500 hover:bg-red-50 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                카드 삭제
              </button>
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <p className="text-gray-400 text-sm mb-3">등록된 카드가 없습니다</p>
              <p className="text-xs text-gray-400 mb-4">미리 카드를 등록해두면 예약 시 바로 사용할 수 있습니다.</p>
              <button onClick={handleRegisterCard} disabled={registeringCard}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3182f6] hover:bg-[#1b64da] text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500">
                <span className="text-base font-bold">T</span>
                {registeringCard ? '처리 중...' : '토스페이먼츠 카드 등록'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
